import Cookies from 'cookies-js'
import { isUndefined, mapValues, omitBy } from 'lodash-es'
import type { paths, operations } from '@23/openapi'
import { ENV_SETTINGS } from '../envSettings'
import { getSiteData } from '../blocs/useSite'
import { getLiveId } from '../roomInfo/roomInfo'
import { getWorkspaceToken } from './tokens'

const API_BASE_URL =
  ENV_SETTINGS.environment === 'production'
    ? 'https://api.twentythree.com'
    : 'https://api.dev.twentythree.com'

/// ========= util types ========== ///

type OmitNever<T> = { [K in keyof T as T[K] extends never ? never : K]: T[K] }

type OptionalIfUndefined<T> = undefined extends T ? [param?: T] : [param: T]

type GetRelativePath<AbsolutePath extends string> =
  AbsolutePath extends `/api/{organizationId}/{workspaceId}/${infer Path}`
    ? Path
    : never

type GetRelativeWebinarPath<AbsolutePath extends string> =
  AbsolutePath extends `/api/${'{organizationId}' | 'public'}/{workspaceId}/live/{liveId}/${infer Path}`
    ? Path
    : never

type InjectId<Path extends string> =
  Path extends `${infer Before}{${'id' | `${string}Id`}}${infer After}`
    ? `${Before}${number}${After}`
    : Path

type Paths = InjectId<GetRelativePath<keyof paths>>
type WebinarPaths = InjectId<GetRelativeWebinarPath<keyof paths>>

type FullPathsMap = {
  [FullPath in keyof paths as InjectId<GetRelativePath<FullPath>>]: FullPath
}

type FullWebinarPathsMap = {
  [FullPath in keyof paths as InjectId<
    GetRelativeWebinarPath<FullPath>
  >]: FullPath
}

/// ========= request types ========== ///

type AcceptedMethod = 'get' | 'put' | 'post' | 'delete' | 'patch'

type Methods<Path extends keyof FullPathsMap> = keyof OmitNever<
  Required<paths[FullPathsMap[Path]]>
> &
  AcceptedMethod

type WebinarMethods<Path extends keyof FullWebinarPathsMap> = keyof OmitNever<
  Required<paths[FullWebinarPathsMap[Path]]>
> &
  AcceptedMethod

type Operation<
  Path extends keyof FullPathsMap,
  Method extends Methods<Path>,
> = NonNullable<paths[FullPathsMap[Path]][Method]>

type WebinarOperation<
  Path extends keyof FullWebinarPathsMap,
  Method extends WebinarMethods<Path>,
> = NonNullable<paths[FullWebinarPathsMap[Path]][Method]>

type RequestData<
  Op extends operations[keyof operations],
  Method extends AcceptedMethod,
> = Method extends 'get'
  ? Op['parameters']['query']
  : Op['requestBody'] extends Record<string, unknown>
    ? Op['requestBody']['content']['application/json']
    : undefined

/// ========= response types ========== ///

type AcceptCode = 200 | 201
type ErrorCode = 403 | 404 | 500
type ResponseCode = AcceptCode | ErrorCode

type Response<
  Op extends operations[keyof operations],
  Code extends ResponseCode,
> =
  Op['responses'] extends Record<
    Code & keyof Op['responses'],
    Record<'content', Record<'application/json', unknown>>
  >
    ? Op['responses'][Code &
        keyof Op['responses']]['content']['application/json']
    : undefined

/// ========== Requests handling ========== ///

const workspaceApiRequest = async <
  Path extends Paths,
  Method extends Methods<Path>,
>(
  path: Path,
  method: Method,
  ...data: OptionalIfUndefined<RequestData<Operation<Path, Method>, Method>>
): Promise<Response<Operation<Path, Method>, AcceptCode>> => {
  const siteData = await getSiteData()
  const token = await getWorkspaceToken()

  return performRequest(
    `/api/${siteData.subscription_id}/${siteData.site_id}/${path}`,
    token,
    method,
    data?.[0]
  )
}

const webinarApiRequest = async <
  Path extends WebinarPaths,
  Method extends WebinarMethods<Path>,
>(
  path: Path,
  method: Method,
  isPublic: boolean = false,
  ...data: OptionalIfUndefined<
    RequestData<WebinarOperation<Path, Method>, Method>
  >
): Promise<Response<WebinarOperation<Path, Method>, AcceptCode>> => {
  const liveId = getLiveId()
  const siteData = await getSiteData()
  const token = await getWorkspaceToken()

  const url = isPublic
    ? `/api/public/${siteData.site_id}/live/${liveId}/${path}`
    : `/api/${siteData.subscription_id}/${siteData.site_id}/live/${liveId}/${path}`

  return performRequest(url, token, method, data?.[0])
}

const authenticatedWebinarApiRequest = async <
  Path extends WebinarPaths,
  Method extends WebinarMethods<Path>,
>(
  path: Path,
  method: Method,
  ...data: OptionalIfUndefined<
    RequestData<WebinarOperation<Path, Method>, Method>
  >
): Promise<Response<WebinarOperation<Path, Method>, AcceptCode>> => {
  return webinarApiRequest(path, method, false, ...data)
}

const publicWebinarApiRequest = async <
  Path extends WebinarPaths,
  Method extends WebinarMethods<Path>,
>(
  path: Path,
  method: Method,
  ...data: OptionalIfUndefined<
    RequestData<WebinarOperation<Path, Method>, Method>
  >
): Promise<Response<WebinarOperation<Path, Method>, AcceptCode>> => {
  return webinarApiRequest(path, method, true, ...data)
}

const performRequest = async (
  path: string,
  bearerToken: string | undefined,
  method: AcceptedMethod,
  data?: Record<string, unknown>
) => {
  const fetchOptions = {
    method: method.toUpperCase(),
    headers: new Headers(),
  } as const

  if (bearerToken) {
    fetchOptions.headers.set('Authorization', `Bearer ${bearerToken}`)
  }
  // setting dev environment cookie as a header to circumvent cross-domain issues
  const cookie = Cookies.get('_tt_dev_env')
  if (cookie) {
    fetchOptions.headers.set('TwentyThree-Development-Environment', cookie)
  }

  // clean up unset data — explicit nulls used for unsetting values
  const cleanData = omitBy(data, isUndefined)

  const url = new URL(path, API_BASE_URL)

  let response
  if (['head', 'get'].includes(method)) {
    const stringifiedData = mapValues(cleanData, (value) => `${value}`)

    response = await fetch(
      `${url.href}?${new URLSearchParams(stringifiedData)}`,
      { ...fetchOptions }
    )
  } else {
    fetchOptions.headers.set('Content-Type', 'application/json')
    response = await fetch(url.href, {
      body: JSON.stringify(cleanData),
      ...fetchOptions,
    })
  }

  if (response.status >= 400 && response.status < 600) {
    const apiError = await response.json()
    throw new ApiError(path, method, apiError)
  }

  try {
    return await response.json()
  } catch (e) {
    throw new Error('Failed to parse response')
  }
}

/// ========= Errors ========== ///

class ApiError<Data> extends Error {
  endpoint: string

  method: string

  response: Data

  constructor(endpoint: string, method: string, response: Data) {
    super('API responded with an error.')
    this.endpoint = endpoint
    this.method = method
    this.response = response
  }
}

const isApiError = <Path extends Paths, Method extends Methods<Path>>(
  error: unknown,
  _path?: Path,
  method?: Method
): error is ApiError<Response<Operation<Path, Method>, ErrorCode>> => {
  return (
    error instanceof ApiError && (!method || error.method === method)
    // && error.endpoint === path TODO:
  )
}

type ValidationErrors = {
  errors: Record<string, Array<{ message: string }>>
}
// TODO: we should avoid the cast in the future
// maybe expose the response type through the openAPI types
const getValidationErrors = (error: unknown) => {
  try {
    if (
      error instanceof ApiError &&
      error.response.code === 'validationError'
    ) {
      return (error.response as ValidationErrors).errors
    }
  } catch (e) {
    console.error(e)
  }

  return null
}

export {
  workspaceApiRequest,
  authenticatedWebinarApiRequest,
  publicWebinarApiRequest,
  isApiError,
  getValidationErrors,
}
